/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IRequestContextUrl } from '@msdyn365-commerce/core';
import { Alert, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import * as React from 'react';

import { BusinessPartner, BusinessPartnerUser, CatalogsDataActions, CommerceException } from '@msdyn365-commerce/retail-proxy';
import { setRenderingServiceCookie, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { IBusinessPartnersData } from './business-partners.data';
import { IBusinessPartnersProps } from './business-partners.props.autogenerated';
import { BusinessPartnerUsersTable } from './components/business-partner-users-table';
import { BusinessPartnersTable } from './components/business-partners-table';

/**
 * Props for the module view.
 */
export interface IBusinessPartnersViewProps extends IBusinessPartnersProps<IBusinessPartnersData> {
    businessPartnersProps: IModuleProps;
    businessPartnersHeading?: React.ReactNode;
    businessPartnerUsersHeading?: React.ReactNode;
    businessPartnersTable?: React.ReactNode;
    businessPartnerUsersTable?: React.ReactNode;
    backToBusinessPartnersLink?: React.ReactNode;
    catalogLoadingIndicator?: React.ReactNode;
    catalogLoadingErrorMessage?: React.ReactNode;
    selectedBusinessPartnerUser?: BusinessPartnerUser;
    cookieConsentNotGivenErrorMessage?: React.ReactNode;
}

/**
 * State of BusinessPartners component.
 */
export interface IBusinessPartnersState {
    selectedBusinessPartner?: BusinessPartner;
    selectedBusinessPartnerUser?: BusinessPartnerUser;
    isLoadingCatalog?: boolean;
    loadCatalogErrorMessage?: string;
    isRetailB2BContactMultiOutletOrderingFeatureEnabled?: boolean;
}

/**
 * The class name root for the module.
 */
export const moduleClassName: string = 'ms-business-partners';

/**
 * BusinessPartners component.
 */
class BusinessPartners extends React.Component<IBusinessPartnersProps<IBusinessPartnersData>, IBusinessPartnersState> {
    private readonly organizationSelectionCookiePrefix = '_msdyn365___org_selection_';
    private readonly retailB2BContactMultiOutletOrderingFeatureName = 'Dynamics.AX.Application.RetailB2BContactMultiOutletOrderingFeature';

    constructor(props: IBusinessPartnersProps<IBusinessPartnersData>) {
        super(props);
        this.state = {};
    }

    public async componentDidMount() {
        const featureState = (await this.props.data.featureState) ?? [];

        this.props.context.request.cookies.removeAccountSelectionCookie();
        this.setState({
            isRetailB2BContactMultiOutletOrderingFeatureEnabled:
                featureState.find(feature => feature.Name === this.retailB2BContactMultiOutletOrderingFeatureName)?.IsEnabled ?? false
        });
    }

    public shouldComponentUpdate(nextProps: IBusinessPartnersProps<IBusinessPartnersData>, nextState: IBusinessPartnersState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const {
            config: { className, pageSize, businessPartnersHeading, businessPartnerUsersHeading }
        } = this.props;
        const viewProps: IBusinessPartnersViewProps = {
            ...this.props,
            selectedBusinessPartnerUser: this.state.selectedBusinessPartnerUser,
            businessPartnersProps: {
                moduleProps: this.props,
                className: classname(moduleClassName, className)
            },
            businessPartnersHeading: !this.state.selectedBusinessPartner && businessPartnersHeading && businessPartnersHeading.text && (
                <Msdyn365.Text
                    className={`${moduleClassName}__heading`}
                    tag={businessPartnersHeading.tag}
                    text={businessPartnersHeading.text}
                />
            ),
            businessPartnerUsersHeading: this.state.selectedBusinessPartner &&
                businessPartnerUsersHeading &&
                businessPartnerUsersHeading.text && (
                    <Msdyn365.Text
                        className={`${moduleClassName}__heading`}
                        tag={businessPartnerUsersHeading.tag}
                        text={businessPartnerUsersHeading.text}
                    />
                ),
            businessPartnersTable: !this.state.selectedBusinessPartner ? (
                <BusinessPartnersTable
                    {...this.props}
                    pageSize={pageSize}
                    className={`${moduleClassName}__table-list`}
                    onSelect={this.onSelectBusinessPartner}
                />
            ) : (
                undefined
            ),
            businessPartnerUsersTable: this.state.selectedBusinessPartner ? (
                <BusinessPartnerUsersTable
                    {...this.props}
                    pageSize={pageSize}
                    className={`${moduleClassName}__table-list`}
                    onSelect={this.onSelectBusinessPartnerUser}
                    businessPartner={this.state.selectedBusinessPartner}
                />
            ) : (
                undefined
            ),
            backToBusinessPartnersLink: this.state.selectedBusinessPartner ? (
                <a
                    className={`${moduleClassName}__back-link`}
                    role='button'
                    onClick={() => {
                        this.onSelectBusinessPartner();
                    }}
                >
                    {this.props.resources.backToBusinessPartnersLink}
                </a>
            ) : (
                undefined
            ),
            catalogLoadingIndicator: this.state.isLoadingCatalog ? (
                <span className={`${moduleClassName}__loading`}>{this.props.resources.loadingLabel}</span>
            ) : (
                undefined
            ),
            catalogLoadingErrorMessage: this.state.loadCatalogErrorMessage ? (
                <span className={`${moduleClassName}__alert`}>{this.state.loadCatalogErrorMessage}</span>
            ) : (
                undefined
            ),
            cookieConsentNotGivenErrorMessage: !this.props.context.request.cookies.isConsentGiven() ? (
                <Alert color='danger'>{this.props.resources.cookieConsentRequiredMessage}</Alert>
            ) : (
                undefined
            )
        };
        return this.props.renderView(viewProps);
    }

    /**
     * Event handler of business partner selection.
     * @param businessPartner - Selected business partner.
     */
    private readonly onSelectBusinessPartner = (businessPartner?: BusinessPartner) => {
        this.setState({
            selectedBusinessPartner: businessPartner
        });
    };

    /**
     * Event handler of business partner user selection.
     * @param businessPartnerUser - Selected business partner user.
     */
    private readonly onSelectBusinessPartnerUser = async (businessPartnerUser: BusinessPartnerUser) => {
        await this._setSelectionCookies(businessPartnerUser);
        this.setState({ selectedBusinessPartnerUser: businessPartnerUser });

        const catalogId = await this.getCustomerDefaultCatalogId(businessPartnerUser);
        validateCatalogId(catalogId);

        const homeUrl = Msdyn365.getUrlSync('home', this.props.context.actionContext);
        if (homeUrl && Msdyn365.msdyn365Commerce.isBrowser && catalogId !== undefined) {
            const fullUrl = new URL(homeUrl, window.location.href);
            fullUrl.searchParams.set('catalogid', `${catalogId}`);
            window.location.href = fullUrl.href;
            return;
        }

        this._removeSelectionCookies();
    };

    /**
     * Sets the appropriate selection cookies based on the active feature path.
     * B2BContact: sets organization selection cookies via rendering service.
     * Legacy: sets account selection cookie directly.
     */
    private readonly _setSelectionCookies = async (businessPartnerUser: BusinessPartnerUser): Promise<void> => {
        if (this.state.isRetailB2BContactMultiOutletOrderingFeatureEnabled) {
            await this._setOrganizationSelectionCookie(businessPartnerUser);
        } else if (businessPartnerUser.AccountNumber) {
            this.props.context.request.cookies.setAccountSelectionCookie(true, businessPartnerUser.AccountNumber);
        }
    };

    /**
     * Removes legacy account selection cookie when redirect fails. Only applies to non-B2BContact flow.
     * New B2BContact cookies are bound to the user's token lifecycle and do not need manual removal.
     */
    private readonly _removeSelectionCookies = (): void => {
        if (!this.state.isRetailB2BContactMultiOutletOrderingFeatureEnabled) {
            this.props.context.request.cookies.removeAccountSelectionCookie();
        }
    };

    /**
     * Get the first catalog ID of the selected customer.
     * For B2B contact flow, the organization selection cookies have not yet been processed by the
     * rendering service at this point, so CAN and CPI must be set on the user context directly
     * to ensure the getCatalogs API call includes the required headers.
     * @param businessPartnerUser - The selected business partner user.
     */
    private readonly getCustomerDefaultCatalogId = async (businessPartnerUser: BusinessPartnerUser): Promise<number | undefined> => {
        this.setState({
            isLoadingCatalog: true
        });

        const requestContext = this.props.context.request;
        const channelId = requestContext.apiSettings.channelId ? Number(requestContext.apiSettings.channelId) : 0;

        if (requestContext.user.isB2bContact) {
            requestContext.user.customerAccountNumber = businessPartnerUser.OrganizationCustomerAccountNumber;
            requestContext.user.contactPersonId = businessPartnerUser.ContactPersonId;
        }

        try {
            const productCatalogs = await CatalogsDataActions.getCatalogsAsync(
                { callerContext: this.props.context.actionContext },
                channelId,
                true
            );
            this.setState({
                isLoadingCatalog: false
            });
            return productCatalogs[0]?.RecordId || 0;
        } catch (e) {
            this.setState({
                isLoadingCatalog: false,
                loadCatalogErrorMessage:
                    (e as { data: CommerceException })?.data?.LocalizedMessage || this.props.resources.genericErrorMessage
            });
            return undefined;
        }
    };

    private readonly _setOrganizationSelectionCookie = async ({
        OrganizationCustomerAccountNumber,
        ContactPersonId
    }: BusinessPartnerUser): Promise<void> => {
        if (!this.state.isRetailB2BContactMultiOutletOrderingFeatureEnabled || !this.props.context.request.cookies.isConsentGiven()) {
            return;
        }

        const {
            context: {
                actionContext: {
                    requestContext: { url }
                },
                telemetry
            }
        } = this.props;

        if (!OrganizationCustomerAccountNumber || !ContactPersonId) {
            telemetry.error('Missing OrganizationCustomerAccountNumber or ContactPersonId for cookie storage.');
            return;
        }

        const cookieOrigin = this._getCurrentUrl(url).origin;
        const prefix = this.organizationSelectionCookiePrefix;

        await Promise.all([
            setRenderingServiceCookie(cookieOrigin, `${prefix}CAN`, OrganizationCustomerAccountNumber),
            setRenderingServiceCookie(cookieOrigin, `${prefix}ContactId`, ContactPersonId)
        ]).catch(error => {
            telemetry.trace(error);
        });
    };

    private readonly _getCurrentUrl = ({ requestUrl: { href } }: IRequestContextUrl): URL => {
        if (Msdyn365.msdyn365Commerce.isBrowser && typeof window !== 'undefined') {
            const browserHref = window.location?.href;

            if (browserHref) {
                try {
                    return new URL(browserHref);
                } catch {
                    return new URL(browserHref, href);
                }
            }
        }

        return new URL(href);
    };
}

export default BusinessPartners;
